import React, { useEffect, useState } from "react";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import { AiOutlineHome } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import SyncProgressBar from "./SyncProgressBar";

dayjs.extend(utc);
dayjs.extend(timezone);

const OrdersManagementPage = () => {
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [selectedShop, setSelectedShop] = useState("");
  const [shops, setShops] = useState([]);
  const [notes, setNotes] = useState({});
  const [statusFilter, setStatusFilter] = useState("awaiting_shipment");
  const [summary, setSummary] = useState({ todayCount: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [tags, setTags] = useState([]);
  const [fetchingAll, setFetchingAll] = useState(false);
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [logSteps, setLogSteps] = useState([]);

  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const [syncSummary, setSyncSummary] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    axios
      .get(`${settings.apiURL}/get_shipstation_tags.php`, {
        headers: authHeader(),
      })
      .then((res) => setTags(res.data.tags || []))

      .catch(() => setTags([]));
  }, []);

  useEffect(() => {
    applySearchFilter();
  }, [searchQuery, allOrders]);

  useEffect(() => {
    if (sortConfig.key) {
      sortOrders(sortConfig.key, sortConfig.direction);
    }
  }, [sortConfig]);

  useEffect(() => {
    if (selectedShop) {
      getFromCacheOnly(selectedShop);
    }
  }, [selectedShop, statusFilter]);

  const fetchShops = () => {
    axios
      .get(`${settings.apiURL}/admin_get_all_shops.php`, {
        headers: authHeader(),
      })
      .then((res) => {
        if (res.data.success) {
          setShops(res.data.shops);
          const savedShopId = localStorage.getItem("adminLastShop");
          if (
            savedShopId &&
            res.data.shops.some((s) => s.shop_id === savedShopId)
          ) {
            setSelectedShop(savedShopId); // savedShopId seçildiğinde getFromCacheOnly çağrılır
          } else {
            setSelectedShop("all"); // Eğer yoksa, tüm mağaza verilerini çekecek
          }
        }
      })
      .catch(() => console.error("Failed to fetch shops"));
  };

  const fetchOrdersSmart = async () => {
    setFetchingAll(true);
    setSyncMessage("🔄 Refreshing cache...");
    setLogSteps([]);

    try {
      const response = await fetch(
        `${settings.apiURL}/refresh_order_cache.php`,
        {
          method: "GET",
          headers: authHeader(),
        }
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let lines = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        lines += chunk;

        const chunks = lines.split("\n");
        lines = chunks.pop(); // incomplete line

        for (const line of chunks) {
          try {
            const parsed = JSON.parse(line);

            if (parsed.step) {
              setSyncMessage(parsed.step);
              setLogSteps((prev) => [...prev, parsed.step]);
            }
          } catch (err) {
            console.warn("Fetch parse error:", line);
          }
        }
      }

      setSyncMessage("📦 Loading cached orders...");

      const cachedRes = await axios.get(
        `${settings.apiURL}/get_cached_orders.php`,
        {
          headers: authHeader(),
        }
      );

      if (cachedRes.data.success && Array.isArray(cachedRes.data.orders)) {
        const enriched = enrichOrders(cachedRes.data.orders);
        setOrders(enriched);
        setAllOrders(enriched);
        countToday(enriched);
        setSyncMessage(null);
      } else {
        toast({ title: "No orders found in cache", icon: "info" });
        setOrders([]);
        setAllOrders([]);
        setSyncMessage(null);
      }
    } catch (error) {
      toast({ title: "Fetch failed", icon: "error" });
      setSyncMessage(null);
    }

    setFetchingAll(false);
  };

  const getFromCacheOnly = async (shopId = null) => {
    setFetchingAll(true);
    setSyncMessage("📦 Loading cached orders...");

    const url = shopId
      ? `${settings.apiURL}/get_cached_orders.php?store_id=${shopId}`
      : `${settings.apiURL}/get_cached_orders.php`; // Eğer shopId varsa, filtrelenmiş olarak çekiyoruz

    try {
      const res = await axios.get(url, {
        headers: authHeader(),
      });

      if (res.data.success && Array.isArray(res.data.orders)) {
        const enriched = enrichOrders(res.data.orders);
        setOrders(enriched);
        setAllOrders(enriched);
        countToday(enriched);
      } else {
        toast({ title: "No cached orders", icon: "info" });
        setOrders([]);
        setAllOrders([]);
      }
    } catch (error) {
      console.error("Cache load error", error);
      toast({ title: "Cache fetch failed", icon: "error" });
    }

    setSyncMessage(null);
    setFetchingAll(false);
  };

  const enrichOrders = (orders) => {
    return orders.map((order) => {
      const baseDate = order.orderDate || order.createDate;
      return {
        ...order,
        baseDate,
        age: calculateAge(baseDate),
        rawAgeHours: calculateRawAge(baseDate),
        shopName: order.shopName || "No Shop Name", // Shop name default
        customerName: order.shipTo?.name || "No Customer Name", // Customer name default
        requestedShippingService:
          order.requestedShippingService || "No Shipping Service", // Requested Service default
      };
    });
  };

  const countToday = (orderList) => {
    const today = dayjs().format("YYYY-MM-DD");
    const todayCount = orderList.filter((o) =>
      (o.baseDate || "").startsWith(today)
    ).length;
    setSummary({ todayCount });
  };

  const calculateAge = (createDate) => {
    const created = dayjs.tz(createDate, "America/Chicago");
    const now = dayjs().tz("America/Chicago");
    const diffInHours = now.diff(created, "hour");
    if (diffInHours < 24) return `${diffInHours}h`;
    const days = Math.floor(diffInHours / 24);
    const hours = diffInHours % 24;
    return `${days}d ${hours}h`;
  };

  const calculateRawAge = (createDate) => {
    const created = dayjs.tz(createDate, "America/Chicago");
    const now = dayjs().tz("America/Chicago");
    return now.diff(created, "hour");
  };

  const getAgeCellStyle = (hours) => {
    if (hours < 12) return { backgroundColor: "#d4edda", color: "#155724" };
    if (hours < 24) return { backgroundColor: "#fff3cd", color: "#856404" };
    return { backgroundColor: "#f8d7da", color: "#721c24" };
  };

  const applySearchFilter = () => {
    if (searchQuery.trim() === "") {
      setOrders(allOrders);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = allOrders.filter(
      (o) =>
        o.orderNumber?.toLowerCase().includes(query) ||
        o.shipTo?.name?.toLowerCase().includes(query)
    );
    setOrders(filtered);
  };

  const sortOrders = (key, direction) => {
    const sorted = [...orders].sort((a, b) => {
      let valA, valB;

      // Sıralama için her kolonun veri yapısına göre işlem yapıyoruz
      if (key === "tags") {
        // tags dizisinin ilk elemanındaki 'name' değerini sıralıyoruz
        valA = a.tagIds?.[0] || ""; // tagIds dizisinin ilk elemanındaki ID
        valB = b.tagIds?.[0] || ""; // tagIds dizisinin ilk elemanındaki ID
      }
      // "shopName" sıralaması
      else if (key === "shopName") {
        valA = a.shopName?.toLowerCase() || "";
        valB = b.shopName?.toLowerCase() || "";
      }
      // "requestedShippingService" sıralaması
      else if (key === "requestedShippingService") {
        valA = a.requestedShippingService?.toLowerCase() || "";
        valB = b.requestedShippingService?.toLowerCase() || "";
      }
      // "internalNotes" sıralaması
      else if (key === "internalNotes") {
        valA = a.internalNotes?.toLowerCase() || "";
        valB = b.internalNotes?.toLowerCase() || "";
      }
      // "is_placed" sıralaması (boolean değerleri 1 ve 0'a dönüştürme)
      else if (key === "is_placed") {
        valA = a.is_placed ? 1 : 0;
        valB = b.is_placed ? 1 : 0;
      }
      // "design_uploaded" sıralaması (boolean değerleri 1 ve 0'a dönüştürme)
      else if (key === "design_uploaded") {
        valA = a.design_uploaded ? 1 : 0;
        valB = b.design_uploaded ? 1 : 0;
      }
      // "orderNumber" sıralaması
      else if (key === "orderNumber") {
        valA = a.orderNumber?.toLowerCase() || "";
        valB = b.orderNumber?.toLowerCase() || "";
      }
      // "shipTo.name" sıralaması
      else if (key === "shipTo.name") {
        valA = a.shipTo?.name?.toLowerCase() || "";
        valB = b.shipTo?.name?.toLowerCase() || "";
      }
      // "age" sıralaması (yaş/süre)
      else if (key === "age") {
        valA = a.age || 0;
        valB = b.age || 0;
      }
      // "createDate" sıralaması
      else if (key === "createDate") {
        valA = new Date(a.createDate) || new Date();
        valB = new Date(b.createDate) || new Date();
      }
      // Diğer sıralama işlemleri
      else {
        valA = typeof a[key] === "string" ? a[key].toLowerCase() : a[key];
        valB = typeof b[key] === "string" ? b[key].toLowerCase() : b[key];
      }

      // Sıralama işlemi
      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setOrders(sorted);
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleNoteChange = (orderId, value) => {
    setNotes((prev) => ({ ...prev, [orderId]: value }));
  };

  const handleNoteBlur = (order) => {
    const updatedNote = notes[order.orderId];
    if (updatedNote === order.internalNotes) return;

    axios
      .post(
        `${settings.apiURL}/update_order_notes.php`,
        { orderId: order.orderId, internalNote: updatedNote },
        { headers: authHeader() }
      )
      .then((res) => {
        if (res.data.success) {
          toast({ title: "Note updated", icon: "success" });

          if (selectedShop === "all") {
            fetchOrdersSmart(); // cache sistemine göre karar verir
          } else {
            fetchOrdersSmart(); // her durumda cache kontrol ediyor zaten
          }
        } else {
          toast({ title: "Update failed", icon: "error" });
        }
      })
      .catch(() => {
        toast({ title: "An error occurred", icon: "error" });
      });
  };

  const SyncMessageBar = ({ message }) => {
    if (!message) return null;
    return (
      <div className="alert alert-info text-center py-2" role="alert">
        {message}
      </div>
    );
  };
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map((o) => o.orderId));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelectOne = (orderId) => {
    if (selectedOrders.includes(orderId)) {
      setSelectedOrders(selectedOrders.filter((id) => id !== orderId));
    } else {
      setSelectedOrders([...selectedOrders, orderId]);
    }
  };

  return (
    <>
      <SyncMessageBar message={syncMessage} />
      <SyncProgressBar progress={syncProgress} />
      {syncSummary && (
        <div className="alert alert-success my-3">
          <strong>Sync Completed:</strong>
          <br />
          Updated: {syncSummary.updated_total}
          <br />
          Shipped: {syncSummary.shipped} | Cancelled: {syncSummary.cancelled} |
          On Hold: {syncSummary.on_hold} | Unknown: {syncSummary.unknown} |
          Other: {syncSummary.other_status}
        </div>
      )}

      <div className="container-fluid mt-4">
        {/* Home butonunu sabitleme ve ortalama */}
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate("/home")}
          style={{
            position: "absolute",
            top: "45px", // Sync bar ile çakışmaması için 45px aşağıda olması sağlanacak
            left: "50%",
            transform: "translateX(-50%)", // Ortalamak için
            zIndex: 9999, // Diğer öğelerden ön planda olması için
          }}
        >
          <AiOutlineHome className="me-2" /> Home
        </button>

        {/* Butonların ve form elemanlarının hizalanması */}
        <div
          className="d-flex justify-content-start align-items-center mb-3 flex-wrap"
          style={{
            paddingTop: "80px", // Üstten biraz daha mesafe bırakıyoruz
          }}
        >
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-primary"
              disabled={fetchingAll || syncing}
              onClick={() => {
                fetchOrdersSmart(true);
              }}
            >
              Fetch All Orders
            </button>

            <select
              className="form-select w-auto"
              value={selectedShop}
              disabled={fetchingAll || syncing}
              onChange={(e) => {
                const newShopId = e.target.value;
                setSelectedShop(newShopId);
                localStorage.setItem("adminLastShop", newShopId);
              }}
            >
              <option value="all">All Shops</option>
              {shops.map((shop) => (
                <option key={shop.shop_id} value={shop.shop_id}>
                  {shop.shop_name}
                </option>
              ))}
            </select>

            <input
              type="text"
              className="form-control w-auto"
              placeholder="Search order # or name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-3">
          <strong>Total Orders:</strong> {orders.length}
        </div>

        <table className="table table-bordered">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={toggleSelectAll}
                  style={{ marginRight: "6px" }}
                />
                #
              </th>

              <th
                onClick={() => requestSort("orderNumber")}
                style={{ cursor: "pointer" }}
              >
                PO Number
              </th>
              <th
                onClick={() => requestSort("shopName")}
                style={{ cursor: "pointer" }}
              >
                Shop
              </th>

              <th
                onClick={() => requestSort("shipTo.name")}
                style={{ cursor: "pointer" }}
              >
                Customer Name
              </th>
              <th
                onClick={() => requestSort("requestedShippingService")}
                style={{ cursor: "pointer" }}
              >
                Requested Service
              </th>
              <th
                onClick={() => requestSort("tags")}
                style={{ cursor: "pointer" }}
              >
                Tags
              </th>

              <th
                onClick={() => requestSort("rawAgeHours")}
                style={{ cursor: "pointer" }}
              >
                Age
              </th>
              <th
                onClick={() => requestSort("internalNotes")}
                style={{ cursor: "pointer" }}
              >
                Internal Note
              </th>
              <th
                onClick={() => requestSort("is_placed")}
                style={{ cursor: "pointer" }}
              >
                Is Placed
              </th>
              <th
                onClick={() => requestSort("design_uploaded")}
                style={{ cursor: "pointer" }}
              >
                Order Uploaded
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => (
              <tr key={order.orderId}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.orderId)}
                    onChange={() => toggleSelectOne(order.orderId)}
                    style={{ marginRight: "6px" }}
                  />
                  {index + 1}
                </td>

                <td>
                  <span
                    style={{
                      color: "blue",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() =>
                      navigate(`/admin-order-details/${order.orderId}`, {
                        state: { orders, selectedShop },
                      })
                    }
                  >
                    {order.orderNumber}
                  </span>
                </td>
                <td>{order.shopName}</td>
                <td>{order.shipTo?.name || ""}</td>
                <td>{order.requestedShippingService || ""}</td>
                <td>
                  {order.tagIds?.length > 0
                    ? order.tagIds
                        .map((id) => {
                          const tag = tags.find(
                            (t) => String(t.tagId) === String(id)
                          );
                          return tag?.name || null;
                        })
                        .filter(Boolean)
                        .join(", ")
                    : ""}
                </td>

                <td style={getAgeCellStyle(order.rawAgeHours)}>{order.age}</td>
                <td>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={notes[order.orderId] || ""}
                    onChange={(e) =>
                      handleNoteChange(order.orderId, e.target.value)
                    }
                    onBlur={() => handleNoteBlur(order)}
                  />
                </td>
                <td>{order.is_placed ? "✅" : "❌"}</td>
                <td>{order.design_uploaded ? "✅" : "❌"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default OrdersManagementPage;
